import { Divider, Box, Button, Card, CardActions, CardContent, Grid, Pagination, Typography, Dialog, DialogTitle, DialogContentText, DialogContent, DialogActions } from "@mui/material";
import { useIlpContext } from "../hooks/useIlpContext";
import { useLearnerContext } from "../hooks/useLearnerContext";
import { useState } from "react";
import { ILearningPlan } from "../types/interfaces/ILearningPlan";
import { ILearningPlanListProps } from "../types/interfaces/ILearningPlanListProps";
import { IIndividualLearningPlan } from "../types/interfaces/IIndividualLearningPlan";
import useDeleteIlp from "../hooks/useDeleteIlp";

//This component is responsible for displaying a list of the learning plans.
const LearningPlanList: React.FC<ILearningPlanListProps> = ({ onSelect, onDelete }) =>  {
    const { isInProcess } = useIlpContext();
    const { individualLearningPlans } = useLearnerContext();
    const learningPlans: ILearningPlan | any = individualLearningPlans?.flatMap(plan => plan.LearningPlan);

    if (learningPlans === null || (learningPlans.length === 1 && learningPlans[0] === null)) return null;

    const [page, setPage] = useState(1);
    const plansPerPage = 3;
    const count = Math.ceil(learningPlans.length/plansPerPage);

    const [showDialog, setShowDialog] = useState(false);
    const [learningPlanToDelete, setLearningPlanToDelete] = useState<ILearningPlan | null>(null);

    const API_DELETE_ILP = process.env.REACT_APP_API_DELETE_ILP;
    const apiDeleteIlp = `${API_DELETE_ILP}`;
    const deleteIlpFetch = useDeleteIlp(apiDeleteIlp);

    //Function to handle the page change.
    const pageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    //Function to handle a button click.
    const handleClick = (planKey: string) => {
        const selectedIlp : IIndividualLearningPlan = individualLearningPlans?.find(plan => plan.LearningPlan.PlanKey === planKey)!;
        onSelect(selectedIlp);
    };

    //Function to handle a plan delete.
    const handleDelete = async (learningPlan: ILearningPlan) => {
        const ilpToDelete : IIndividualLearningPlan = individualLearningPlans?.find(plan => plan.LearningPlan.PlanKey === learningPlan.PlanKey)!;
        const isIlpPlanDeleted = await deleteIlpFetch(ilpToDelete);
        if (isIlpPlanDeleted) {
            onDelete(ilpToDelete.LearningPlan.PlanKey);
        }
        setShowDialog(false);
    };

    //Function to show a delete dialog.
    const showDeleteDialog = (learningPlan: ILearningPlan) => {
        setLearningPlanToDelete(learningPlan);
        setShowDialog(true);
    };

    //Function to handle a delete dialog confirm.
    const confirmDelete = () => {
        if (learningPlanToDelete) {
            handleDelete(learningPlanToDelete);
        }
    };
    
    return (
        <>
            <Typography variant="h6" color="black" component="div">
                <Box sx={{ fontWeight: 'bold' }}>Learning Plans</Box>
            </Typography>
            <Grid container spacing={1} sx={{mt:1}}>
                {learningPlans.slice((page - 1) * plansPerPage, page * plansPerPage).map((plan: ILearningPlan[] | any ) => (
                    <Grid item xs={12} sm={6} md={4} key={plan.PlanKey} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Card sx={{ border: 1, borderColor: 'divider' }}>
                            <CardContent sx={{ paddingBottom: 0 }}>
                                <Box display="flex" alignItems="center">
                                    <Typography variant="body2" fontWeight="bold" color="black" mr={1}>ILP Name:</Typography>
                                    <Typography variant="body2" component="div" sx={{wordBreak: 'break-word'}}>{plan.PlanName}</Typography>
                                </Box>
                            </CardContent>
                            <CardActions>
                                <Grid container spacing={1}>
                                {plan.PlanStatus === 'Started' && (
                                    <Grid item>
                                        <Button variant="contained" size="medium"       color="primary" 
                                        onClick={() => handleClick(plan.PlanKey)} 
                                        disabled={isInProcess}
                                        sx={{ ml:1, maxWidth: 100, fontSize:18}} >Edit</Button>
                                    </Grid>
                                )}
                                {(plan.PlanStatus === 'Approved' || plan.PlanStatus === "Submitted") && (
                                    <Grid item>
                                        <Button variant="contained" color="primary" 
                                        onClick={() => handleClick(plan.PlanKey)} 
                                        disabled={isInProcess}
                                        sx={{ml:1, maxWidth: 100, fontSize:18}}>View</Button>
                                    </Grid>
                                )}
                                {(plan.PlanStatus === 'Started' || plan.PlanStatus === "Submitted") && (
                                    <Grid item>
                                        <Button variant="contained" size="medium"  color="primary" 
                                        onClick={() => showDeleteDialog(plan)} 
                                        disabled={isInProcess}
                                        sx={{ ml:1, maxWidth: 100, fontSize:18}} >Delete</Button>
                                    </Grid>
                                )}
                                </Grid>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Pagination count={count} page={page} onChange={pageChange} variant="outlined" shape="rounded" showFirstButton showLastButton sx={{mt:1, mb:5}} />
            <Divider sx={{mt:1, mb:5}}></Divider>
            <Dialog open={showDialog}>
                <DialogTitle>{"Confirm Deletion"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this plan?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDialog(false)} color="primary">No</Button>
                    <Button onClick={confirmDelete} color="primary" autoFocus disabled={isInProcess}>Yes</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default LearningPlanList;